import { Facility } from '@reliance/types/models/Facility';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { alertStore } from '../alert/alertStore';
import AddDeviceChargerCredential from './AddDeviceChargerCredential';
import api from '../common/api';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import MobileDevicesTable from '../mobile-device/Table';
import FacilityProxyForm, { ProxySettings } from '../mobile-device/FacilityProxyForm';

type FacilityProfilePayload = {
  data?: string;
  payloadIdentifier?: string;
};

type FacilityDevicesTabProps = {
  facility: Facility;
};

const FacilityDevicesTab: React.FC<FacilityDevicesTabProps> = ({ facility }) => {
  const [showProxy, setShowProxy] = useState(false);
  const [facilityProfile, setFacilityProfile] = useState<FacilityProfilePayload | null>(null);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const [confirmDeleteProxy, setConfirmDeleteProxy] = useState(false);

  useNotifyTabLoading('devices', fetchingProfile || savingProfile || deletingProfile);

  const loadProfile = useCallback(async () => {
    setFetchingProfile(true);
    try {
      const res = await api.get<unknown>(`/mobile-device-profile/by-identifier/facility-profile?facilityId=${facility.id}`);
      setFacilityProfile(res.data as FacilityProfilePayload);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
      if (
        ax.response?.status === 404 &&
        (ax.response?.data?.error?.message?.indexOf('Profile not found') ?? -1) > -1
      ) {
        setFacilityProfile(null);
      } else {
        setFacilityProfile(null);
      }
    } finally {
      setFetchingProfile(false);
    }
  }, [facility.id]);

  const toggleProxyEdit = useCallback(() => {
    if (showProxy) {
      setShowProxy(false);
      return;
    }
    setShowProxy(true);
    void loadProfile();
  }, [loadProfile, showProxy]);

  const saveFacilityProxy = useCallback(
    async (proxySettings: ProxySettings) => {
      setSavingProfile(true);
      try {
        const res = await api.post<{ profile?: FacilityProfilePayload }>('/mobile-device-profile/facility', {
          proxyServer: proxySettings.server,
          proxyPort: parseInt(proxySettings.port, 10),
          proxyUsername: proxySettings.username,
          proxyPassword: proxySettings.password,
          facilityId: facility.id,
        });
        setFacilityProfile(res.data.profile ?? null);
        alertStore.success('The proxy was successfully configured');
      } catch {
        alertStore.error('An unexpected error occurred while configuring the proxy');
      } finally {
        setSavingProfile(false);
      }
    },
    [facility.id]
  );

  const deleteFacilityProxy = useCallback(async () => {
    const payloadId = facilityProfile?.payloadIdentifier;
    if (payloadId == null) {
      return;
    }
    setDeletingProfile(true);
    try {
      await api.delete(`/mobile-device-profile/facility/${facility.id}?payloadIdentifier=${payloadId}`);
      setShowProxy(false);
      setFacilityProfile(null);
      alertStore.success('The proxy was successfully deleted');
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
      if (
        ax.response?.status === 404 &&
        (ax.response?.data?.error?.message?.indexOf('Profile not found') ?? -1) > -1
      ) {
        setFacilityProfile(null);
      }
      alertStore.error('An unexpected error occurred while deleting the proxy');
    } finally {
      setDeletingProfile(false);
      setConfirmDeleteProxy(false);
    }
  }, [facility.id, facilityProfile?.payloadIdentifier]);

  const outboundHref = `/shipping-ticket/outbound/create/${facility.id}`;
  const inboundHref = `/shipping-ticket/inbound/create/${facility.id}`;

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <Link className="btn btn-secondary me-2" to={outboundHref}>
                Create Shipping Ticket
              </Link>
              <Link className="btn btn-secondary me-2" to={inboundHref}>
                Create Return Ticket
              </Link>
              <Button variant="secondary" className="me-2" onClick={toggleProxyEdit} disabled={fetchingProfile}>
                Configure Proxy
                {fetchingProfile && (
                  <span>
                    {' '}
                    <i className="fas fa-spin fa-spinner" />
                  </span>
                )}
              </Button>
              <AddDeviceChargerCredential facility={facility} />
            </div>

            {showProxy && !fetchingProfile && (
              <div className="mt-3">
                <FacilityProxyForm
                  facilityProfile={facilityProfile}
                  saving={savingProfile}
                  deleting={deletingProfile}
                  onSave={saveFacilityProxy}
                  onDelete={() => setConfirmDeleteProxy(true)}
                />
              </div>
            )}

            <div className="mt-2">
              <MobileDevicesTable
                facilityId={facility.id}
                hiddenColumns={['adminAppVersion', 'infoAppVersion', 'kioskAppVersion', 'messagingAppVersion']}
              />
            </div>
          </div>
        </div>
      </div>

      {confirmDeleteProxy && (
        <ConfirmModal
          show
          title="Remove Proxy"
          acceptText="Remove"
          onClose={() => setConfirmDeleteProxy(false)}
          onAccept={deleteFacilityProxy}>
          Removing the proxy requires close coordination with devices and the network. Improper removal may result in
          devices becoming unreachable. Are you sure you want to remove it?
        </ConfirmModal>
      )}
    </div>
  );
};

export default FacilityDevicesTab;
